import React, { useState } from 'react';
import axios from 'axios';
import type { ReviewCycle } from './types';
import RenameDialog from './RenameDialog';

interface UnlinkLineageButtonProps {
  cycle: ReviewCycle;
  // Caller passes whether the current user holds manage_submissions or
  // admin role — mirrors the backend PATCH /unlink-lineage gate. DM does
  // NOT inherit this per D3.
  canUnlink: boolean;
  getAuthHeaders: () => Promise<Record<string, string>>;
  onComplete: () => void;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

/**
 * Phase 8C remainder — admin / submission-manager action to FORK a cycle
 * from its lineage. Combines an unlink (clear previousVersion) with a
 * mandatory rename so the cycle's triple (Purpose / Audience / Title) no
 * longer collides with the rest of the lineage — otherwise the producer
 * dashboard would still group them together and the suggestion logic
 * would still treat them as the same content thread.
 *
 * Renders only when:
 *  - the user holds content_review.manage_submissions or admin role
 *  - the cycle has a previousVersion link (no-op otherwise)
 *
 * UI: click opens a styled modal (shared RenameDialog) pre-filled with
 * the current triple. Title change is required. Submit calls the backend
 * which performs rename + unlink atomically and rewrites the SharePoint
 * canonical filename for file-based cycles. Single audit row with
 * action='unlink_rename'.
 */
const UnlinkLineageButton: React.FC<UnlinkLineageButtonProps> = ({ cycle, canUnlink, getAuthHeaders, onComplete }) => {
  const [open, setOpen] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);

  if (!canUnlink) return null;
  if (!cycle.previousVersion) return null;

  const handleSubmit = async (changes: {
    newName?: string;
    newType?: string;
    newAudience?: string;
    reason: string;
  }) => {
    const headers = await getAuthHeaders();
    const res = await axios.patch(
      `${backendUrl}/api/content-review/cycles/${cycle._id}/unlink-lineage`,
      changes,
      { headers }
    );
    if (res.data?.sharePointRenameError) {
      // Surface as a non-blocking warning — the lineage was forked + Mongo
      // state is correct; only the SharePoint canonical filename failed.
      setWarning(`Lineage forked, but SharePoint file rename failed: ${res.data.sharePointRenameError}`);
    } else {
      setWarning(null);
    }
    onComplete();
  };

  return (
    <>
      <div style={{ display: 'inline-block', marginLeft: 8 }}>
        <button
          className="review-btn"
          style={{
            background: '#fff',
            border: '1px solid #856404',
            color: '#856404',
            padding: '6px 12px',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 14
          }}
          onClick={() => setOpen(true)}
          title="Fork this cycle's lineage (admin / submission manager only) — rename + unlink atomically"
        >
          Fork lineage
        </button>
        {warning && <div style={{ color: '#856404', fontSize: 12, marginTop: 4 }}>{warning}</div>}
      </div>
      {open && (
        <RenameDialog
          title="Fork lineage"
          introCopy="This cycle was auto-linked to an earlier cycle with the same Purpose / Audience / Title. To truly start a fresh lineage, give it a distinguishing title here. The SharePoint file is also renamed canonically."
          submitLabel="Fork lineage"
          reasonLabel="Reason for forking (audited)"
          reasonPlaceholder="e.g. same title was used for genuinely different content; auto-match chained to wrong predecessor"
          requireNameChange
          currentName={cycle.contentItem.name || ''}
          currentType={cycle.contentItem.type || ''}
          currentAudience={cycle.contentItem.audience || ''}
          getAuthHeaders={getAuthHeaders}
          onSubmit={handleSubmit}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export default UnlinkLineageButton;
